import { Badge } from '@/components/ui/badge';
import * as Icons from 'lucide-react';
import type { SectionSpec } from './sections';

interface Props {
  sections: SectionSpec[];
  activeId: string;
  dirtyBySection: Record<string, number>;
  onActivate: (id: string) => void;
}

function iconFor(name: string) {
  const map: Record<string, any> = Icons;
  const camel = name.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join('');
  return map[camel] ?? Icons.Settings;
}

export function JumpNavStrip({ sections, activeId, dirtyBySection, onActivate }: Props) {
  const total = Object.values(dirtyBySection).reduce((a, b) => a + b, 0);
  return (
    <div
      className="sticky top-0 z-10 flex flex-wrap items-center gap-1.5 rounded-t-lg border-b bg-card px-5 py-3"
    >
      <span className="mr-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Jump to
      </span>
      {sections.map((s) => {
        const Icon = iconFor(s.icon);
        const active = s.id === activeId;
        const dirty = dirtyBySection[s.id] ?? 0;
        return (
          <a
            key={s.id}
            href={`#section-${s.id}`}
            aria-current={active ? 'true' : undefined}
            onClick={(e) => { e.preventDefault(); onActivate(s.id); }}
            className={[
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium no-underline',
              active
                ? 'border-primary bg-[color-mix(in_srgb,var(--color-primary)_10%,var(--color-card))] text-primary'
                : 'border-border bg-card text-foreground',
            ].join(' ')}
          >
            <Icon className="h-3 w-3" aria-hidden />
            {s.title}
            {dirty > 0 && (
              <span
                aria-label={`${dirty} unsaved fields`}
                className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground"
              >
                {dirty}
              </span>
            )}
          </a>
        );
      })}
      {total > 0 && (
        <Badge variant="default" className="ml-auto">
          {total} unsaved
        </Badge>
      )}
    </div>
  );
}
